import { PresenceStatus } from "../constants/presenceStatus"

export interface Practice {
  id: string
  name: string
  placeName: string
  startTime: string
  endTime: string
  userPresence: PresenceStatus
}

export interface Festival {
  id: string
  name: string
  placeName: string
  startTime: string
  endTime: string
  enabled: boolean
  userPresence: PresenceStatus
}

export interface PracticeListResponse {
  practiceList: [Practice]
}

export interface FestivalListResponse {
  festivalList: [Festival]
}

export interface PracticePresenceResponse {
  practicePresence: {
    practice: {
      id: string
      name: string
      placeName: string
      startTime: string
      endTime: string
    }
    members: {
      name: string
      presenceStatus: string
      updatedAt: string
      memo: string
    }[]
    authorizedUserPresence: {
      name: string
      presenceStatus: string
      updatedAt: string
      memo: string
    }
    guestList: {
      guestId: string
      guestName: string
      guestEmail: string
      memo: string
      updatedAt: string
      creatorName: string
    }[]
  }
}

export interface PutPracticePresenceData {
  practiceId: string
  userEmail: string
  presenceStatus: string
  presenceMemo: string
}

export interface PutFestivalPresenceData {
  festivalId: string
  userEmail: string
  presenceStatus: string
  presenceMemo: string
}

export interface NewPracticeGuestData {
  practiceId: string
  guestEmail: string
  guestName: string
  memo: string
}

export interface UpdatePracticeGuestData {
  practiceId: string
  guestId: string
  guestEmail: string
  guestName: string
  memo: string
}

export interface DeletePracticeGuestData {
  practiceId: string
  guestId: string
}

export interface InternalPracticePresenceResponse {
  practicePresence: {
    practice: {
      name: string
      placeName: string
      startTime: string
      endTime: string
    }
    members: {
      name: string
      email: string
      presenceStatus: string
      updatedAt: string
      memo: string
    }[]
    guestList: {
      guestId: string
      guestName: string
      guestEmail: string
      memo: string
      updatedAt: string
      creatorName: string
    }[]
  }
}

export interface FestivalPresenceResponse {
  festivalPresence: {
    festival: {
      id: string
      name: string
      placeName: string
      startTime: string
      endTime: string
    }
    presentMembers: {
      name: string
      updatedAt: string
    }[]
    absentMembers: {
      name: string
      updatedAt: string
    }[]
    members: {
      name: string
      presenceStatus: string
      updatedAt: string
      memo: string
    }[]
    authorizedUserPresence: {
      name: string
      presenceStatus: string
      updatedAt: string
      memo: string
    }
  }
}

export interface RefreshTokenResponse {
  access_token: string
  expires_in: number
  scope: string
  token_type: string
}

const apiUrl = "https://d15wik05ic63zs.cloudfront.net"

export async function getPracticeList(accessToken: string): Promise<PracticeListResponse | null> {
  const url = `${apiUrl}/api/practiceList`
  try {
    const response = await fetch(url, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch repository: ${response.status}`)
    }

    return await response.json() as PracticeListResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function getPracticePresence(practiceId: string, accessToken: string): Promise<PracticePresenceResponse | null> {
  const url = `${apiUrl}/api/practice?practiceId=${practiceId}`

  try {
    const response = await fetch(url, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as PracticePresenceResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function putPracticePresence(
  data: PutPracticePresenceData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/practicePresence`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function putFestivalPresence(
  data: PutFestivalPresenceData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/festivalPresence`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}


export async function getInternalPracticePresence(practiceId: string, accessToken: string): Promise<InternalPracticePresenceResponse | null> {
  const url = `${apiUrl}/api/internal/practice?practiceId=${practiceId}`

  try {
    const response = await fetch(url, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as InternalPracticePresenceResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function getFestivalList(accessToken: string): Promise<FestivalListResponse | null> {
  const url = `${apiUrl}/api/festivalList`
  try {
    const response = await fetch(url, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch repository: ${response.status}`)
    }

    return await response.json() as FestivalListResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function getFestivalPresence(festivalId: string, accessToken: string): Promise<FestivalPresenceResponse | null> {
  const url = `${apiUrl}/api/festival?festivalId=${festivalId}`

  try {
    const response = await fetch(url, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as FestivalPresenceResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

export interface GoogleToken {
  access_token: string
  expires_in: number
  refresh_token: string
  scope: string
  token_type: string
  id_token: string
}

export interface GoogleTokenResponse {
  google_token: GoogleToken,
  roles: [string]
}

export async function getGoogleToken(code: string, redirectUrl: string): Promise<GoogleTokenResponse | null> {
  const url = `${apiUrl}/api/google/getToken?code=${code}&redirect_url=${redirectUrl}`

  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as GoogleTokenResponse
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function refreshGoogleToken(refreshToken: string): Promise<RefreshTokenResponse | null> {
  const url = `${apiUrl}/api/google/refreshToken?refresh_token=${refreshToken}`

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }
    return await response.json() as RefreshTokenResponse;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return null;
  }
}

export async function putPracticeGuest(
  data: NewPracticeGuestData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/newPracticeGuest`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function updatePracticeGuest(
  data: UpdatePracticeGuestData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/updatePracticeGuest`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function deletePracticeGuest(
  data: DeletePracticeGuestData,
  accessToken: string
): Promise<any> {
  const url = `${apiUrl}/api/deletePracticeGuest`
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch api: ${response.status}`)
    }

    return await response.json();
  } catch (error) {
    console.error(error)
    return null
  }
}